import React, { useContext, useEffect, useState } from 'react';
import { AccountCreateFormWrapper } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateFormWrapper';
import { AccountCreateTabs } from '@components-member/TradingAccounts/AccountCreate/components/AccountCreateTabs/AccountCreateTabs';
import { NotificationBlock } from '@components-member/TradingAccounts/AccountCreate/components/NotificationBlock';
import { AccountCreateContext } from '@components-member/TradingAccounts/AccountCreate/context/accountCreateContext';
import { ACCOUNT_CREATE_STEP } from '@components-member/TradingAccounts/AccountCreate/interfaces';
import { TradingPlatforms } from '@components-member/TradingAccounts/TradingPlatforms';
import { Modal } from '@components-shared/modal';
import Preloader from '@components-shared/preloader';
import { TRADING_ACCOUNT_SOURCE } from 'global.enums';
import { getTitle } from './helpers';
import styles from './styles.module.scss';

interface IAccountCreateModal {
  closeModalHandler: () => void;
  processing: boolean;
}

export const AccountCreateModal = ({ closeModalHandler, processing }: IAccountCreateModal): JSX.Element => {
  const { isAccountLimitForUnverifiedUsers, openAccountType, formOpeningSource } = useContext(AccountCreateContext);

  const { step, setStep, isBackButtonDisplayed } = useModalStepInitialization();

  const title = getTitle(formOpeningSource, openAccountType);
  const isSidebarSource = formOpeningSource === TRADING_ACCOUNT_SOURCE.SIDEBAR;
  const isPlatformDisplaying = step === ACCOUNT_CREATE_STEP.PLATFORMS;

  const renderContent = () => {
    if (processing) {
      return <Preloader />;
    }

    if (isPlatformDisplaying) {
      return <TradingPlatforms changeStep={setStep} />;
    }

    if (isSidebarSource) {
      return (
        <AccountCreateFormWrapper
          closeModalHandler={closeModalHandler}
          accountType={openAccountType}
          changeStep={setStep}
          isBackButtonDisplayed={isBackButtonDisplayed}
        />
      );
    }

    return (
      <AccountCreateTabs
        isBackButtonDisplayed={isBackButtonDisplayed}
        openAccountType={openAccountType}
        changeStep={setStep}
        closeModalHandler={closeModalHandler}
      />
    );
  };

  return (
    <Modal
      title={title}
      closeHandler={closeModalHandler}
      classNameContainer={styles.tradingAccountModal}
      notification={
        <NotificationBlock isNotificationDisplayed={isAccountLimitForUnverifiedUsers} onLinkClick={closeModalHandler} />
      }>
      {renderContent()}
    </Modal>
  );
};

const useModalStepInitialization = () => {
  const { accountCreateState } = useContext(AccountCreateContext);

  const { creationSetting } = accountCreateState;

  const hasEnabledPlatforms = Boolean(creationSetting.tradingPlatformBrandingSettings.length > 0);

  const [step, setStep] = useState<ACCOUNT_CREATE_STEP>(ACCOUNT_CREATE_STEP.CREATE);

  useEffect(() => {
    setStep(hasEnabledPlatforms ? ACCOUNT_CREATE_STEP.PLATFORMS : ACCOUNT_CREATE_STEP.CREATE);
  }, [hasEnabledPlatforms]);

  return {
    step,
    setStep,
    isBackButtonDisplayed: hasEnabledPlatforms,
  };
};
